'use client'

import { useRouter, useSearchParams } from 'next/navigation'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../global-config'
import {
  urlResolverGetAccount,
  urlResolverGetCart,
  urlResolverGetCheckoutAddress,
} from '../url-handling'

import {
  CART_ACTION,
  CHECKOUT_ADDRESS_TARGET_PAGE_CODE,
  LOGIN_SOURCE_PAGE,
  TARGET_PAGE_CODE_PARAM,
  TARGET_PAGE_REDIRECT_PARAM,
} from './consts'

const addOriginParameter = (path: string, sourcePage: string): string => {
  try {
    const fallbackHostname = 'https://www.shop-apotheke.com'
    const url = new URL(path, fallbackHostname)
    url.searchParams.append('after', sourcePage)

    if (url.origin === fallbackHostname) {
      return url.pathname + url.search
    }

    return url.origin + url.pathname + url.search
  } catch {
    return path
  }
}

/**
 * Safely converts any provided URL into a domain-relative path
 * (e.g., "/some/path?query=123#hash") by discarding the external host.
 *
 * If the URL is invalid or cannot be parsed, returns the fallback URL.
 */
const getSafeRelativeUrl = (
  url: string,
  fallbackUrl: string,
  shopUrl: string,
): string => {
  try {
    const { hash, pathname, search } = new URL(url, shopUrl)

    return `${pathname}${search}${hash}`
  } catch {
    return fallbackUrl
  }
}

/**
 * Determines the final redirect URL based on target page codes,
 * source page, and optional loginRedirectUrl.
 */
const getDestinationUrl = ({
  globalConfig,
  loginRedirectUrl,
  sourcePage,
  targetPageCode,
}: {
  globalConfig: PublicGlobalConfig
  loginRedirectUrl: string
  sourcePage: string
  targetPageCode: string
}): string => {
  const { language, shopUrl, tenantAndEnv } = globalConfig

  if (
    targetPageCode === CHECKOUT_ADDRESS_TARGET_PAGE_CODE &&
    sourcePage === LOGIN_SOURCE_PAGE
  ) {
    return urlResolverGetCart(globalConfig, CART_ACTION.CHECK_OVERDRAFTS)
  }

  if (loginRedirectUrl) {
    const fallback = urlResolverGetAccount(globalConfig)
    const safeRelativeUrl = getSafeRelativeUrl(
      loginRedirectUrl,
      fallback,
      shopUrl,
    )

    return addOriginParameter(safeRelativeUrl, sourcePage)
  }

  const destinationUrl =
    targetPageCode === CHECKOUT_ADDRESS_TARGET_PAGE_CODE
      ? urlResolverGetCheckoutAddress(globalConfig, tenantAndEnv, language)
      : urlResolverGetAccount(globalConfig)

  return addOriginParameter(destinationUrl, sourcePage)
}

/**
 * This custom hook redirects the user to a target page based on the URL search parameters and the source page
 * @param sourcePage eg: login
 * eg: for login from checkout
 *  - URL /nx/login?targetPageCode=checkoutAddress
 *  - const redirectToTargetPage = useGetRedirectToTargetPage('login')
 *
 */
export const useGetRedirectToTargetPage = (sourcePage = ''): (() => void) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const globalConfig = useGlobalConfigContext()

  const loginRedirectUrl = searchParams.get(TARGET_PAGE_REDIRECT_PARAM) ?? ''
  const targetPageCode = searchParams.get(TARGET_PAGE_CODE_PARAM) ?? ''

  const redirectUrl = getDestinationUrl({
    globalConfig,
    loginRedirectUrl,
    sourcePage,
    targetPageCode,
  })

  // Return a function that performs the actual navigation
  return () => {
    router.push(redirectUrl)
  }
}
